import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { assign, createMachine } from "xstate";
import { css, keyframes } from "@nju/css";
import {
  fetchGroups,
  GroupsContext,
  IGroup,
} from "@nju/data-access-portal-dashboard";
import {
  fetchMember,
  IMemberOrchestrators,
  MemberOrchestratorContext,
} from "@nju/data-access-portal-member";
import {
  IOfferModeWithFSecure,
  useOfferModeWithFsecureWithoutFallback,
} from "@nju/data-access-portal-offers";
import {
  fetchOrchestrators,
  IOrchestrators,
  OrchestratorsContext,
} from "@nju/data-access-portal-orchestrator";
import {
  fetchOrder,
  IOrder,
  OrderContext,
} from "@nju/data-access-portal-order";
import {
  fetchPaymentHistory,
  IPaymentHistory,
  PaymentsHistoryContext,
} from "@nju/data-access-portal-payment";
import {
  fetchOrderProducts,
  IFetchOrderProductsResponse,
  IServiceStatus,
  MemberProductsContext,
  ProductsContext,
} from "@nju/data-access-portal-product";
import {
  fetchTasks,
  IFetchTasksResponse,
  ITasks,
  MemberServiceStatusContext,
  MemberTasksContext,
  ServiceStatusContext,
  TasksContext,
} from "@nju/data-access-portal-service";
import { IInfoResponse, info, UserContext } from "@nju/data-access-portal-user";
import {
  getFSecureServiceStatus,
  getFtthServiceStatus,
  getMobileServiceStatus,
} from "@nju/feature-portal-service";
import { BasicError } from "@nju/result";
import { CircleLoader, GeneralErrorNotification } from "@nju/ui";
// import SockJsClient from "react-stomp";

const MAX_ERROR_COUNT = 2;

const EMPTY_PRODUCTS = {
  ftthProducts: {
    sfh: undefined,
    modem: undefined,
    internet: undefined,
    access: undefined,
    cyberSecurity: undefined,
    additionalTask: undefined,
    commitment: undefined,
    hasNoProducts: true,
    hasProducts: false,
    isProductsUndefined: false,
  },
  mobileProducts: {
    dataPackage: undefined,
    simCard: undefined,
    voicePackage: undefined,
    mobileAccess: undefined,
    _5g: undefined,
    hasNoProducts: true,
    hasProducts: false,
    isProductsUndefined: false,
    television: undefined,
  },
  fSecureProducts: {
    isProductsUndefined: false,
    hasNoProducts: true,
    hasProducts: false,
    fSecure: undefined,
  },
};

interface IContext {
  orchestrators?: IOrchestrators;
  memberOrchestrators?: IMemberOrchestrators;
  products?: IFetchOrderProductsResponse;
  serviceStatus: {
    ftthServiceStatus: IServiceStatus["ftthServiceStatus"];
    mobileServiceStatus: IServiceStatus["mobileServiceStatus"];
    fSecureServiceStatus: IServiceStatus["fSecureServiceStatus"];
  };
  memberProducts?: IFetchOrderProductsResponse;
  memberServiceStatus: {
    ftthServiceStatus: IServiceStatus["ftthServiceStatus"];
    mobileServiceStatus: IServiceStatus["mobileServiceStatus"];
    fSecureServiceStatus: IServiceStatus["fSecureServiceStatus"];
  };
  user?: IInfoResponse;
  paymentHistory?: IPaymentHistory;
  error?: BasicError;
  refreshInterval?: number;
  order?: IOrder;
  tasks: ITasks;
  memberTasks: ITasks;
  groups?: IGroup[];
  flow: "multiOrder" | undefined;
  userId?: string | null | undefined;
  mode: (IOfferModeWithFSecure | "base") | undefined;
  errorCount: number;
}

type IEvent =
  | { type: "MEMBER_PRODUCTS"; memberProducts: IFetchOrderProductsResponse }
  | { type: "PRODUCTS"; products: IFetchOrderProductsResponse }
  | { type: "ORDER"; order?: IOrder }
  | { type: "TASKS"; tasks: IFetchTasksResponse }
  | { type: "MEMBER_TASKS"; memberTasks: IFetchTasksResponse | undefined }
  | { type: "GROUPS"; groups?: IGroup[] }
  | { type: "FORCE_FETCH_MEMBER"; userId: string }
  | {
      type: "FORCE_FETCH_MEMBER_WITH_MODE";
      userId: string;
      mode: IOfferModeWithFSecure;
    }
  | { type: "FORCE_FETCH_MULTI_ONBOARDING_CONTEXT"; flow: "multiOrder" }
  | { type: "FORCE_CHANGE_MODE"; mode: IOfferModeWithFSecure }
  | {
      type: "MEMBER_ORCHESTRATOR";
      memberOrchestrators?: IMemberOrchestrators;
    }
  | { type: "ERROR"; error: BasicError }
  | { type: "NO_MEMBER_PRODUCTS" }
  | { type: "NO_PRODUCTS" }
  | { type: "FETCH_ORCHESTRATORS_SUCCESS"; orchestrators: IOrchestrators }
  | {
      type: "REQUIRED_MOBILE_DATA";
      user: IInfoResponse;
      orchestrators: IOrchestrators;
      tasks: IFetchTasksResponse;
      groups?: IGroup[];
    }
  | {
      type: "REQUIRED_FTTH_DATA";
      user: IInfoResponse;
      orchestrators: IOrchestrators;
      paymentHistory: IPaymentHistory;
    }
  | {
      type: "REQUIRED_FSECURE_DATA";
      user: IInfoResponse;
      orchestrators: IOrchestrators;
    }
  | {
      type: "FETCH_USER_INFO_SUCCESS";
      user: IInfoResponse;
    }
  | {
      type: "ERROR_COUNT";
    };

const machine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    context: {
      serviceStatus: {
        ftthServiceStatus: undefined,
        mobileServiceStatus: undefined,
        fSecureServiceStatus: undefined,
      },
      memberServiceStatus: {
        ftthServiceStatus: undefined,
        mobileServiceStatus: undefined,
        fSecureServiceStatus: undefined,
      },
      tasks: {
        mnpPortout: undefined,
      },
      memberTasks: {
        mnpPortout: undefined,
      },
      flow: undefined,
      mode: undefined,
      errorCount: 0,
    },
    initial: "resolveMode",
    states: {
      resolveMode: {
        always: [
          {
            cond: "isMobile",
            target: "loadingMobile",
          },
          {
            cond: "isFtth",
            target: "loadingFtth",
          },
          {
            cond: "isFsecure",
            target: "loadingFsecure",
          },
          {
            target: "loadingBase",
          },
        ],
      },
      loadingBase: {
        initial: "products",
        states: {
          products: {
            invoke: {
              src: "fetchProducts",
            },
            on: {
              PRODUCTS: [
                {
                  actions: [
                    "setProducts",
                    "setServiceStatus",
                    "resetErrorCount",
                  ],
                  target: "userInfo",
                },
              ],
              NO_PRODUCTS: [
                {
                  actions: ["setNoProducts", "resetErrorCount"],
                  target: "userInfo",
                },
              ],
              ERROR: {
                actions: "setError",
                target: "#idleBase",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "products",
              },
            },
          },
          userInfo: {
            invoke: {
              src: "fetchUserInfo",
            },
            on: {
              FETCH_USER_INFO_SUCCESS: [
                {
                  actions: ["setUserInfo", "resetErrorCount"],
                  target: "fetchOrchestrators",
                },
              ],
              ERROR: {
                actions: "setError",
                target: "#idleBase",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "userInfo",
              },
            },
          },
          fetchOrchestrators: {
            invoke: {
              src: "fetchOrchestrators",
            },
            on: {
              FETCH_ORCHESTRATORS_SUCCESS: {
                actions: ["setOrchestratorsData", "resetErrorCount"],
                target: "loadGroups",
              },
              ERROR: {
                actions: "setError",
                target: "#idleBase",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "fetchOrchestrators",
              },
            },
          },
          loadGroups: {
            id: "loadGroups",
            invoke: {
              src: "fetchGroups",
            },
            on: {
              GROUPS: {
                actions: ["setGroups", "resetErrorCount"],
                target: "#idleBase",
              },
              ERROR: {
                actions: "setError",
                target: "#idleBase",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "loadGroups",
              },
            },
          },
        },
      },
      idleBase: { id: "idleBase" },
      loadingMobile: {
        id: "loadingMobile",
        initial: "requiredData",
        states: {
          requiredData: {
            invoke: {
              src: "fetchRequiredMobileData",
            },
            on: {
              REQUIRED_MOBILE_DATA: {
                actions: ["setRequiredMobileData", "resetErrorCount"],
                target: "memberOrchestrators",
              },
              ERROR: {
                actions: "setError",
                target: "#idleMobile",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "requiredData",
              },
            },
          },
          memberOrchestrators: {
            invoke: {
              src: "fetchMemberOrchestrator",
            },
            on: {
              MEMBER_ORCHESTRATOR: {
                actions: ["setMemberOrchestrator", "resetErrorCount"],
                target: "memberTasks",
              },
              ERROR: {
                actions: "setError",
                target: "#idleMobile",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "memberOrchestrators",
              },
            },
          },
          memberTasks: {
            invoke: {
              src: "fetchMemberTasks",
            },
            on: {
              MEMBER_TASKS: {
                actions: ["setMemberTasks", "resetErrorCount"],
                target: "products",
              },
              ERROR: {
                actions: "setError",
                target: "#idleMobile",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "memberTasks",
              },
            },
          },
          products: {
            invoke: {
              src: "fetchProducts",
            },
            on: {
              PRODUCTS: [
                {
                  actions: [
                    "setProducts",
                    "setServiceStatus",
                    "resetErrorCount",
                  ],
                  target: "memberProducts",
                },
              ],
              NO_PRODUCTS: [
                {
                  actions: ["setNoProducts", "resetErrorCount"],
                  target: "memberProducts",
                },
              ],
              ERROR: {
                actions: "setError",
                target: "#idleMobile",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "products",
              },
            },
          },
          memberProducts: {
            invoke: {
              src: "fetchMemberProducts",
            },
            on: {
              MEMBER_PRODUCTS: {
                actions: [
                  "setMemberProducts",
                  "setMemberServiceStatus",
                  "resetErrorCount",
                ],
                target: "#idleMobile",
              },
              NO_MEMBER_PRODUCTS: {
                actions: ["setNoMemberProducts", "resetErrorCount"],
                target: "#idleMobile",
              },
              ERROR: {
                actions: "setError",
                target: "#idleMobile",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "memberProducts",
              },
            },
          },
        },
      },
      idleMobile: {
        id: "idleMobile",
        on: {
          FORCE_FETCH_MEMBER: {
            actions: "setUserId",
            target: "loadingMobile",
          },
          FORCE_FETCH_MULTI_ONBOARDING_CONTEXT: {
            target: "loadingMobile",
          },
          FORCE_CHANGE_MODE: {
            actions: "setMode",
            target: "resolveMode",
          },
        },
        after: [
          {
            delay: (context) => {
              return context.refreshInterval || Number.POSITIVE_INFINITY;
            },
            cond: (context) => {
              return !!context.refreshInterval;
            },
            target: "refreshMobileData",
          },
        ],
      },
      refreshMobileData: {
        initial: "fetchOrchestrators",
        on: {
          FORCE_FETCH_MEMBER: {
            actions: "setUserId",
            target: "loadingMobile",
          },
          FORCE_CHANGE_MODE: {
            actions: "setMode",
            target: "resolveMode",
          },
        },
        states: {
          fetchOrchestrators: {
            invoke: {
              src: "fetchOrchestrators",
            },
            on: {
              FETCH_ORCHESTRATORS_SUCCESS: {
                actions: ["setOrchestratorsData", "resetErrorCount"],
                target: "memberOrchestrators",
              },
              ERROR: {
                actions: "setError",
                target: "#idleMobile",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "fetchOrchestrators",
              },
            },
          },
          memberOrchestrators: {
            invoke: {
              src: "fetchMemberOrchestrator",
            },
            on: {
              MEMBER_ORCHESTRATOR: {
                actions: ["setMemberOrchestrator", "resetErrorCount"],
                target: "loadGroups",
              },
              ERROR: {
                actions: "setError",
                target: "#idleMobile",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "memberOrchestrators",
              },
            },
          },
          loadGroups: {
            id: "loadGroups",
            invoke: {
              src: "fetchGroups",
            },
            on: {
              GROUPS: {
                actions: ["setGroups", "resetErrorCount"],
                target: "products",
              },
              ERROR: {
                actions: "setError",
                target: "#idleMobile",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "loadGroups",
              },
            },
          },
          products: {
            invoke: {
              src: "fetchProducts",
            },
            on: {
              PRODUCTS: [
                {
                  actions: [
                    "setProducts",
                    "setServiceStatus",
                    "resetErrorCount",
                  ],
                  target: "memberProducts",
                },
              ],
              NO_PRODUCTS: [
                {
                  actions: ["setNoProducts", "resetErrorCount"],
                  target: "memberProducts",
                },
              ],
              ERROR: {
                actions: "setError",
                target: "#idleMobile",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "products",
              },
            },
          },
          memberProducts: {
            invoke: {
              src: "fetchMemberProducts",
            },
            on: {
              MEMBER_PRODUCTS: {
                actions: [
                  "setMemberProducts",
                  "setMemberServiceStatus",
                  "resetErrorCount",
                ],
                target: "#idleMobile",
              },
              NO_MEMBER_PRODUCTS: {
                actions: ["setNoMemberProducts", "resetErrorCount"],
                target: "#idleMobile",
              },
              ERROR: {
                actions: "setError",
                target: "#idleMobile",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "memberProducts",
              },
            },
          },
        },
      },
      loadingFtth: {
        id: "loadingFtth",
        initial: "requiredData",
        states: {
          requiredData: {
            invoke: {
              src: "fetchRequiredFtthData",
            },
            on: {
              REQUIRED_FTTH_DATA: {
                actions: ["setRequiredFtthData", "resetErrorCount"],
                target: "loadGroups",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFtth",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "requiredData",
              },
            },
          },
          loadGroups: {
            id: "loadGroups",
            invoke: {
              src: "fetchGroups",
            },
            on: {
              GROUPS: {
                actions: ["setGroups", "resetErrorCount"],
                target: "order",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFtth",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "loadGroups",
              },
            },
          },
          order: {
            invoke: {
              src: "fetchOrder",
            },
            on: {
              ORDER: {
                actions: ["setOrder", "resetErrorCount"],
                target: "products",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFtth",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "order",
              },
            },
          },
          products: {
            invoke: {
              src: "fetchProducts",
            },
            on: {
              PRODUCTS: {
                actions: ["setProducts", "setServiceStatus", "resetErrorCount"],
                target: "#idleFtth",
              },
              NO_PRODUCTS: {
                actions: ["setNoProducts", "resetErrorCount"],
                target: "#idleFtth",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFtth",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "products",
              },
            },
          },
        },
      },
      idleFtth: {
        id: "idleFtth",
        on: {
          FORCE_FETCH_MEMBER_WITH_MODE: {
            actions: "setUserIdWithMode",
            target: "loadingMobile",
          },
          FORCE_CHANGE_MODE: {
            actions: "setMode",
            target: "resolveMode",
          },
        },
        after: [
          {
            delay: (context) => {
              return context.refreshInterval || Number.POSITIVE_INFINITY;
            },
            cond: (context) => {
              return !!context.refreshInterval;
            },
            target: "refreshFtthData",
          },
        ],
      },
      refreshFtthData: {
        on: {
          FORCE_CHANGE_MODE: {
            actions: "setMode",
            target: "resolveMode",
          },
        },
        initial: "fetchOrchestrators",
        states: {
          fetchOrchestrators: {
            invoke: {
              src: "fetchOrchestrators",
            },
            on: {
              FETCH_ORCHESTRATORS_SUCCESS: {
                actions: ["setOrchestratorsData", "resetErrorCount"],
                target: "loadGroups",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFtth",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "fetchOrchestrators",
              },
            },
          },
          loadGroups: {
            id: "loadGroups",
            invoke: {
              src: "fetchGroups",
            },
            on: {
              GROUPS: {
                actions: ["setGroups", "resetErrorCount"],
                target: "products",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFtth",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "loadGroups",
              },
            },
          },
          products: {
            invoke: {
              src: "fetchProducts",
            },
            on: {
              PRODUCTS: {
                actions: ["setProducts", "setServiceStatus", "resetErrorCount"],
                target: "#idleFtth",
              },
              NO_PRODUCTS: {
                actions: ["setNoProducts", "resetErrorCount"],
                target: "#idleFtth",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFtth",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "products",
              },
            },
          },
        },
      },
      loadingFsecure: {
        id: "loadingFsecure",
        initial: "requiredData",
        states: {
          requiredData: {
            invoke: {
              src: "fetchRequiredFsecureData",
            },
            on: {
              REQUIRED_FSECURE_DATA: {
                actions: ["setRequiredFsecureData", "resetErrorCount"],
                target: "loadGroups",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFsecure",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "requiredData",
              },
            },
          },
          loadGroups: {
            id: "loadGroups",
            invoke: {
              src: "fetchGroups",
            },
            on: {
              GROUPS: {
                actions: ["setGroups", "resetErrorCount"],
                target: "products",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFsecure",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "loadGroups",
              },
            },
          },
          products: {
            invoke: {
              src: "fetchProducts",
            },
            on: {
              PRODUCTS: {
                actions: ["setProducts", "setServiceStatus", "resetErrorCount"],
                target: "#idleFsecure",
              },
              NO_PRODUCTS: {
                actions: ["setNoProducts", "resetErrorCount"],
                target: "#idleFsecure",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFsecure",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "products",
              },
            },
          },
        },
      },
      idleFsecure: {
        id: "idleFsecure",
        on: {
          FORCE_FETCH_MEMBER_WITH_MODE: {
            actions: "setUserIdWithMode",
            target: "loadingMobile",
          },
          FORCE_CHANGE_MODE: {
            actions: "setMode",
            target: "resolveMode",
          },
        },
        after: [
          {
            delay: (context) => {
              return context.refreshInterval || Number.POSITIVE_INFINITY;
            },
            cond: (context) => {
              return !!context.refreshInterval;
            },
            target: "refreshFsecureData",
          },
        ],
      },
      refreshFsecureData: {
        on: {
          FORCE_CHANGE_MODE: {
            actions: "setMode",
            target: "resolveMode",
          },
        },
        initial: "fetchOrchestrators",
        states: {
          fetchOrchestrators: {
            invoke: {
              src: "fetchOrchestrators",
            },
            on: {
              FETCH_ORCHESTRATORS_SUCCESS: {
                actions: ["setOrchestratorsData", "resetErrorCount"],
                target: "loadGroups",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFsecure",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "fetchOrchestrators",
              },
            },
          },
          loadGroups: {
            id: "loadGroups",
            invoke: {
              src: "fetchGroups",
            },
            on: {
              GROUPS: {
                actions: ["setGroups", "resetErrorCount"],
                target: "products",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFsecure",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "loadGroups",
              },
            },
          },
          products: {
            invoke: {
              src: "fetchProducts",
            },
            on: {
              PRODUCTS: {
                actions: ["setProducts", "setServiceStatus", "resetErrorCount"],
                target: "#idleFsecure",
              },
              NO_PRODUCTS: {
                actions: ["setNoProducts", "resetErrorCount"],
                target: "#idleFsecure",
              },
              ERROR: {
                actions: "setError",
                target: "#idleFsecure",
              },
              ERROR_COUNT: {
                actions: "setErrorCount",
                target: "products",
              },
            },
          },
        },
      },
    },
  },
  {
    guards: {
      isMobile: ({ mode }) => {
        return mode === "mobile" || mode === "postpaid";
      },
      isFtth: ({ mode }) => {
        return mode === "ftth";
      },
      isFsecure: ({ mode }) => {
        return mode === "fsecure";
      },
    },
    actions: {
      setUserId: assign((context, event: IEvent) => {
        if (event.type === "FORCE_FETCH_MEMBER") {
          return {
            userId: event.userId,
          };
        }

        return context;
      }),
      setUserIdWithMode: assign((context, event: IEvent) => {
        if (event.type === "FORCE_FETCH_MEMBER_WITH_MODE") {
          return {
            userId: event.userId,
            mode: event.mode,
          };
        }

        return context;
      }),
      setMode: assign((context, event: IEvent) => {
        if (event.type === "FORCE_CHANGE_MODE") {
          return {
            mode: event.mode,
          };
        }

        return context;
      }),
      setRequiredFtthData: assign((context, event: IEvent) => {
        if (event.type === "REQUIRED_FTTH_DATA") {
          return {
            orchestrators: event.orchestrators,
            user: event.user,
            paymentHistory: event.paymentHistory,
          };
        }

        return context;
      }),
      setRequiredFsecureData: assign((context, event: IEvent) => {
        if (event.type === "REQUIRED_FSECURE_DATA") {
          return {
            orchestrators: event.orchestrators,
            user: event.user,
          };
        }

        return context;
      }),
      setRequiredMobileData: assign((context, event: IEvent) => {
        if (event.type === "REQUIRED_MOBILE_DATA") {
          if (!event.tasks || event.tasks.length === 0) {
            return {
              orchestrators: event.orchestrators,
              user: event.user,
              groups: event.groups,
              tasks: {
                mnpPortout: undefined,
              },
            };
          }

          const mnpPortout = event.tasks.find((task) => {
            return (
              task.type === "MNP_PORTOUT" &&
              (task.state === "ACTIVE" || task.state === "INPROGRESS")
            );
          });

          return {
            orchestrators: event.orchestrators,
            user: event.user,
            groups: event.groups,
            tasks: {
              mnpPortout,
            },
          };
        }

        return context;
      }),
      setOrchestratorsData: assign((context, event: IEvent) => {
        if (event.type === "FETCH_ORCHESTRATORS_SUCCESS") {
          return {
            orchestrators: event.orchestrators,
          };
        }

        return context;
      }),
      setProducts: assign((context, event: IEvent) => {
        if (event.type === "PRODUCTS") {
          return {
            products: event.products,
          };
        }

        return context;
      }),
      setMemberProducts: assign((context, event: IEvent) => {
        if (event.type === "MEMBER_PRODUCTS") {
          return {
            memberProducts: event.memberProducts,
          };
        }

        return context;
      }),
      setMemberServiceStatus: assign((context, event: IEvent) => {
        if (event.type === "MEMBER_PRODUCTS") {
          return {
            memberServiceStatus: {
              ftthServiceStatus: getFtthServiceStatus(
                event.memberProducts.ftthProducts.access,
              ),
              mobileServiceStatus: getMobileServiceStatus(
                event.memberProducts.mobileProducts.mobileAccess,
              ),
              fSecureServiceStatus: getFSecureServiceStatus(
                event.memberProducts.fSecureProducts.fSecure,
              ),
            } as const,
          };
        }

        return context;
      }),
      setServiceStatus: assign((context, event: IEvent) => {
        if (event.type === "PRODUCTS") {
          return {
            serviceStatus: {
              ftthServiceStatus: getFtthServiceStatus(
                event.products.ftthProducts.access,
              ),
              fSecureServiceStatus: getFSecureServiceStatus(
                event.products.fSecureProducts.fSecure,
              ),
              mobileServiceStatus: getMobileServiceStatus(
                event.products.mobileProducts.mobileAccess,
              ),
            } as const,
          };
        }

        return context;
      }),
      setOrder: assign((context, event: IEvent) => {
        if (event.type === "ORDER") {
          return {
            order: event.order,
          };
        }

        return context;
      }),
      setMemberTasks: assign((context, event: IEvent) => {
        if (event.type === "MEMBER_TASKS") {
          if (!event.memberTasks || event.memberTasks.length === 0) {
            return {
              memberTasks: {
                mnpPortout: undefined,
              },
            };
          }

          const mnpPortout = event.memberTasks.find((task) => {
            return (
              task.type === "MNP_PORTOUT" &&
              (task.state === "ACTIVE" || task.state === "INPROGRESS")
            );
          });

          return {
            memberTasks: {
              mnpPortout,
            },
          };
        }

        return context;
      }),
      setGroups: assign((context, event: IEvent) => {
        if (event.type === "GROUPS") {
          return {
            groups: event.groups,
          };
        }

        return context;
      }),
      setMemberOrchestrator: assign((context, event: IEvent) => {
        if (event.type === "MEMBER_ORCHESTRATOR") {
          return {
            memberOrchestrators: event.memberOrchestrators,
          };
        }

        return context;
      }),
      setError: assign((context, event: IEvent) => {
        if (event.type === "ERROR") {
          return {
            error: event.error,
          };
        }

        return context;
      }),
      setErrorCount: assign((context, event: IEvent) => {
        if (event.type === "ERROR_COUNT") {
          return {
            errorCount: context.errorCount + 1,
          };
        }

        return context;
      }),
      resetErrorCount: assign((_, __: IEvent) => {
        return {
          errorCount: 0,
        };
      }),
      setNoMemberProducts: assign((_, __: IEvent) => {
        return {
          memberProducts: EMPTY_PRODUCTS,
          memberServiceStatus: {
            ftthServiceStatus: undefined,
            mobileServiceStatus: undefined,
            fSecureServiceStatus: undefined,
          },
        };
      }),
      setNoProducts: assign((_, __: IEvent) => {
        return {
          products: EMPTY_PRODUCTS,
          serviceStatus: {
            ftthServiceStatus: undefined,
            mobileServiceStatus: undefined,
            fSecureServiceStatus: undefined,
          },
        };
      }),
      setUserInfo: assign((context, event: IEvent) => {
        if (event.type === "FETCH_USER_INFO_SUCCESS") {
          return {
            user: event.user,
          };
        }

        return context;
      }),
    },
    services: {
      fetchRequiredMobileData: ({ errorCount }) => {
        return async (callback) => {
          try {
            const [orchestrators, user, tasks, groups] = await Promise.all([
              fetchOrchestrators(),
              info(),
              fetchTasks({ type: "MNP_PORTOUT" }),
              fetchGroups(),
            ]);

            callback({
              type: "REQUIRED_MOBILE_DATA",
              orchestrators: orchestrators.unwrap(),
              user: user.unwrap(),
              tasks: tasks.unwrap(),
              groups: groups.unwrap(),
            });
          } catch (error) {
            if (error instanceof BasicError) {
              if (errorCount < MAX_ERROR_COUNT) {
                callback({ type: "ERROR_COUNT" });
              } else {
                callback({ type: "ERROR", error });
              }
            }
          }
        };
      },
      fetchRequiredFtthData: ({ errorCount }) => {
        return async (callback) => {
          try {
            const [orchestrators, user, paymentHistory] = await Promise.all([
              fetchOrchestrators(),
              info(),
              fetchPaymentHistory({
                page: 0,
                paymentsPerPage: 15,
                type: ["PAYMENT", "REFUND", "REGISTER"],
              }),
            ]);

            callback({
              type: "REQUIRED_FTTH_DATA",
              orchestrators: orchestrators.unwrap(),
              user: user.unwrap(),
              paymentHistory: paymentHistory.unwrap(),
            });
          } catch (error) {
            if (error instanceof BasicError) {
              if (errorCount < MAX_ERROR_COUNT) {
                callback({ type: "ERROR_COUNT" });
              } else {
                callback({ type: "ERROR", error });
              }
            }
          }
        };
      },
      fetchRequiredFsecureData: ({ errorCount }) => {
        return async (callback) => {
          try {
            const [orchestrators, user] = await Promise.all([
              fetchOrchestrators(),
              info(),
            ]);

            callback({
              type: "REQUIRED_FSECURE_DATA",
              orchestrators: orchestrators.unwrap(),
              user: user.unwrap(),
            });
          } catch (error) {
            if (error instanceof BasicError) {
              if (errorCount < MAX_ERROR_COUNT) {
                callback({ type: "ERROR_COUNT" });
              } else {
                callback({ type: "ERROR", error });
              }
            }
          }
        };
      },
      fetchUserInfo: ({ errorCount }) => {
        return async (callback) => {
          try {
            const user = await info();

            callback({
              type: "FETCH_USER_INFO_SUCCESS",
              user: user.unwrap(),
            });
          } catch (error) {
            if (error instanceof BasicError) {
              if (errorCount < MAX_ERROR_COUNT) {
                callback({ type: "ERROR_COUNT" });
              } else {
                callback({ type: "ERROR", error });
              }
            }
          }
        };
      },
      fetchOrchestrators: ({ errorCount }) => {
        return async (callback) => {
          try {
            const orchestrators = await fetchOrchestrators();

            callback({
              type: "FETCH_ORCHESTRATORS_SUCCESS",
              orchestrators: orchestrators.unwrap(),
            });
          } catch (error) {
            if (error instanceof BasicError) {
              if (errorCount < MAX_ERROR_COUNT) {
                callback({ type: "ERROR_COUNT" });
              } else {
                callback({ type: "ERROR", error });
              }
            }
          }
        };
      },
      fetchProducts: ({ errorCount }) => {
        return async (callback) => {
          const result = await fetchOrderProducts();

          if (result.isOk) {
            if (
              result.value.ftthProducts.hasNoProducts &&
              result.value.mobileProducts.hasNoProducts &&
              result.value.fSecureProducts.hasNoProducts
            ) {
              callback({ type: "NO_PRODUCTS" });
            } else {
              callback({ type: "PRODUCTS", products: result.value });
            }
          } else {
            if (errorCount < MAX_ERROR_COUNT) {
              callback({ type: "ERROR_COUNT" });
            } else {
              callback({ type: "ERROR", error: result.err });
            }
          }
        };
      },
      fetchMemberProducts: ({ userId, errorCount }) => {
        return async (callback) => {
          const result = await fetchOrderProducts(userId);

          if (result.isOk) {
            if (
              result.value.ftthProducts.hasNoProducts &&
              result.value.mobileProducts.hasNoProducts &&
              result.value.fSecureProducts.hasNoProducts
            ) {
              callback({ type: "NO_MEMBER_PRODUCTS" });
            } else {
              callback({
                type: "MEMBER_PRODUCTS",
                memberProducts: result.value,
              });
            }
          } else {
            if (errorCount < MAX_ERROR_COUNT) {
              callback({ type: "ERROR_COUNT" });
            } else {
              callback({ type: "ERROR", error: result.err });
            }
          }
        };
      },
      fetchOrder: (context) => {
        return async (callback) => {
          if (!context.orchestrators?.ftth?.orderId) {
            return callback({ type: "ORDER", order: undefined });
          }

          const result = await fetchOrder({
            orderId: context.orchestrators.ftth.orderId,
            version: "3",
          });

          if (result.isOk) {
            return callback({ type: "ORDER", order: result.value });
          } else {
            if (context.errorCount < MAX_ERROR_COUNT) {
              callback({ type: "ERROR_COUNT" });
            } else {
              callback({ type: "ERROR", error: result.err });
            }
          }
        };
      },
      fetchMemberTasks: ({
        userId,
        memberOrchestrators,
        orchestrators,
        errorCount,
      }) => {
        return async (callback) => {
          const userIdFromOrchestrator =
            memberOrchestrators?.mobile?.userId ||
            orchestrators?.mobile?.userId;
          const resolveUserId = userId ? userId : userIdFromOrchestrator;

          if (resolveUserId) {
            const result = await fetchTasks({
              type: "MNP_PORTOUT",
              userId: resolveUserId,
            });

            if (result.isError) {
              return errorCount < MAX_ERROR_COUNT
                ? callback({ type: "ERROR_COUNT" })
                : callback({ type: "ERROR", error: result.err });
            }

            return callback({
              type: "MEMBER_TASKS",
              memberTasks: result.value,
            });
          }

          return callback({ type: "MEMBER_TASKS", memberTasks: undefined });
        };
      },
      fetchGroups: ({ errorCount }) => {
        return async (callback) => {
          const result = await fetchGroups();

          if (result.isError) {
            return errorCount < MAX_ERROR_COUNT
              ? callback({ type: "ERROR_COUNT" })
              : callback({ type: "ERROR", error: result.err });
          }

          return callback({ type: "GROUPS", groups: result.value });
        };
      },
      fetchMemberOrchestrator: ({
        userId,
        memberOrchestrators,
        orchestrators,
        errorCount,
      }) => {
        return async (callback) => {
          try {
            const userIdFromOrchestrator =
              memberOrchestrators?.mobile?.userId ||
              orchestrators?.mobile?.userId;
            const resolveUserId = userId ? userId : userIdFromOrchestrator;

            if (resolveUserId) {
              const result = (
                await fetchMember({
                  userId: resolveUserId,
                })
              ).unwrap();

              return callback({
                type: "MEMBER_ORCHESTRATOR",
                memberOrchestrators: result,
              });
            }

            return callback({
              type: "MEMBER_ORCHESTRATOR",
              memberOrchestrators: {
                mobile: undefined,
                changeMsisdn: undefined,
                mobileReactivation: undefined,
                tv: undefined,
              },
            });
          } catch (error) {
            if (error instanceof BasicError) {
              if (errorCount < MAX_ERROR_COUNT) {
                callback({ type: "ERROR_COUNT" });
              } else {
                callback({ type: "ERROR", error });
              }
            }
          }
        };
      },
    },
  },
);

interface IRequiredDashboardDataProps {
  children:
    | React.ReactNode
    | ((props: {
        handleSetMultiDuoTrioContext: (flow: "multiOrder") => void;
        handleSetSelectedNumber: (userId: string) => void;
        handleSetMode: (mode: IOfferModeWithFSecure) => void;
        handleSetSelectedNumberWithMode: (
          userId: string,
          mode: IOfferModeWithFSecure,
        ) => void;
        isLoading: boolean;
      }) => React.ReactNode);
  refreshInterval?: number;
  extendedMode?: IOfferModeWithFSecure | "base";
}

export function RequiredDashboardData({
  children,
  refreshInterval,
  extendedMode,
}: IRequiredDashboardDataProps) {
  const router = useRouter();
  const modeWithoutFallback = useOfferModeWithFsecureWithoutFallback();
  const { userId, flow } = router.query;

  const [current, send] = useMachine(machine, {
    context: {
      refreshInterval,
      flow: flow as "multiOrder" | undefined,
      userId: userId as string | undefined,
      mode:
        extendedMode ||
        modeWithoutFallback ||
        (window.sessionStorage.getItem("mode") as IOfferModeWithFSecure),
    },
  });

  const { context, matches } = current;
  const {
    orchestrators,
    user,
    error,
    products,
    serviceStatus,
    memberProducts,
    memberServiceStatus,
    order,
    paymentHistory,
    tasks,
    memberTasks,
    groups,
    memberOrchestrators,
  } = context;

  const handleSetSelectedNumber = React.useCallback(
    (userId: string) => {
      send({ type: "FORCE_FETCH_MEMBER", userId });
    },
    [send],
  );

  const handleSetMultiDuoTrioContext = React.useCallback(
    (flow: "multiOrder") => {
      send({ type: "FORCE_FETCH_MULTI_ONBOARDING_CONTEXT", flow });
    },
    [send],
  );

  const handleSetMode = React.useCallback(
    (mode: IOfferModeWithFSecure) => {
      send({ type: "FORCE_CHANGE_MODE", mode });
    },
    [send],
  );

  const handleSetSelectedNumberWithMode = React.useCallback(
    (userId: string, mode: IOfferModeWithFSecure) => {
      send({ type: "FORCE_FETCH_MEMBER_WITH_MODE", userId, mode });
    },
    [send],
  );

  // const renderSocket = () => {
  //   if (localStorage.getItem("access_token")) {
  //     return (
  //       <SockJsClient
  //         autoReconnect={true}
  //         debug={false}
  //         headers={{
  //           Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //         }}
  //         onMessage={() => send({ type: "REFRESH_DATA" })}
  //         topics={["/user/exchange/amq.direct/notifications"]}
  //         url="https://int-messenger.dopapp.pl/ws"
  //       />
  //     );
  //   }
  //   return undefined;
  // };

  if (error) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return router.reload();
        }}
      />
    );
  }

  if (
    orchestrators === undefined ||
    user === undefined ||
    products === undefined ||
    matches("loadingFtth") ||
    matches("loadingMobile") ||
    matches("loadingFsecure")
  ) {
    return (
      <div className={loaderWrapper()}>
        <CircleLoader />
      </div>
    );
  }

  return (
    <OrchestratorsContext.Provider value={orchestrators}>
      <OrderContext.Provider value={order}>
        <ServiceStatusContext.Provider value={serviceStatus}>
          <ProductsContext.Provider value={products}>
            <GroupsContext.Provider value={groups}>
              <UserContext.Provider value={user}>
                <PaymentsHistoryContext.Provider value={paymentHistory}>
                  <TasksContext.Provider value={tasks}>
                    <MemberOrchestratorContext.Provider
                      value={memberOrchestrators as IMemberOrchestrators}
                    >
                      <MemberProductsContext.Provider
                        value={memberProducts || EMPTY_PRODUCTS}
                      >
                        <MemberServiceStatusContext.Provider
                          value={memberServiceStatus}
                        >
                          <MemberTasksContext.Provider value={memberTasks}>
                            {/* {renderSocket()} */}
                            {typeof children === "function"
                              ? children({
                                  handleSetSelectedNumber,
                                  handleSetMultiDuoTrioContext,
                                  handleSetMode,
                                  handleSetSelectedNumberWithMode,
                                  isLoading:
                                    matches("loadingMobile") ||
                                    matches("loadingFtth") ||
                                    matches("loadingFsecure"),
                                })
                              : children}
                          </MemberTasksContext.Provider>
                        </MemberServiceStatusContext.Provider>
                      </MemberProductsContext.Provider>
                    </MemberOrchestratorContext.Provider>
                  </TasksContext.Provider>
                </PaymentsHistoryContext.Provider>
              </UserContext.Provider>
            </GroupsContext.Provider>
          </ProductsContext.Provider>
        </ServiceStatusContext.Provider>
      </OrderContext.Provider>
    </OrchestratorsContext.Provider>
  );
}

const show = keyframes({
  "0%": { opacity: 0 },
  "1%": { opacity: 0 },
  "100%": { opacity: 1 },
});

const loaderWrapper = css({
  opacity: 0,
  padding: "40px 0",
  display: "flex",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  animationName: `${show}`,
  animationDelay: "0.3s",
  animationDuration: "1s",
  animationFillMode: "forwards",
});
